import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { modalRoutes } from 'constants/routes';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { useAnalyticsSelection } from 'app/modules/analytics/hooks/useAnalyticsSelection';
import { useAnalyticsInventory } from 'app/modules/analytics/hooks/useAnalyticsInventory';
import { useLastUsedAnalyticsProfilesStore } from 'app/modules/analytics/hooks/useLastUsedAnalyticsProfiles';
import ProfileListFooterAction from 'app/modules/analytics/components/sidebar/ProfileListFooterAction';
import ProfileListRow from 'app/modules/analytics/components/sidebar/ProfileListRow';
import ProfileListSection from 'app/modules/analytics/components/sidebar/ProfileListSection';
import type { PageData } from 'types/Page';
import { buildAnalyticsManageProfileGroupSearch } from 'app/modules/analytics/utils/buildAnalyticsManageProfileGroupSearch';

const AnalyticsSidebarLastUsedProfileList: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pages } = useAnalyticsInventory();
  const { selectPage, selectedPageId } = useAnalyticsSelection();
  const [snapshotProfiles] = useState(
    () => useLastUsedAnalyticsProfilesStore.getState().profiles,
  );

  const lastUsedPages = useMemo(
    () =>
      snapshotProfiles
        .map((profile) => pages.find((page) => page.id === profile.id))
        .filter((page): page is PageData => page !== undefined),
    [pages, snapshotProfiles],
  );

  return (
    <ProfileListSection
      title={lastUsedPages.length > 0 ? t('lastUsed') : undefined}
      scrollClassName="tw-max-h-64"
      footer={
        pages.length > 0 ? (
          <ProfileListFooterAction
            icon={faPlus}
            label={t('profileGroup.createProfileGroup')}
            onClick={() =>
              navigate({
                hash: modalRoutes.MANAGE_PROFILE_GROUP,
                search: buildAnalyticsManageProfileGroupSearch(),
              })
            }
            dataCy="analytics_sidebar_create-group"
            tone="neutral"
          />
        ) : undefined
      }
    >
      {lastUsedPages.map((page) => (
        <ProfileListRow
          key={page.id}
          variant="navigable"
          page={page}
          isActive={selectedPageId === page.id}
          onSelect={() => selectPage(page)}
        />
      ))}
    </ProfileListSection>
  );
};

export default AnalyticsSidebarLastUsedProfileList;
